import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_BASE;

export default function Signup() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    country: '',
    detectedCountry: '',
    countryCode: '',
    phoneNumber: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    document.title = "Signup – Ennoverse";
  }, []);

  useEffect(() => {
    const detectCountryInfo = async () => {
      try {
        const geoRes = await fetch('https://ipapi.co/json/');
        const geoData = await geoRes.json();
        const isoCode = geoData.country_code;
        const dialCode = geoData.country_calling_code?.replace('+', '') || '';

        const countryRes = await fetch(`${API_BASE}/api/countries`);
        const allCountries = await countryRes.json();

        const matched = allCountries.find(c => c.isoCode === isoCode);
        const fallback = allCountries.find(c => c.isDefault);

        setFormData(prev => ({
          ...prev,
          country: matched ? matched._id : fallback?._id || '',
          detectedCountry: isoCode,
          countryCode: dialCode
        }));
      } catch (err) {
        console.error('Geo detection failed', err);
      }
    };

    detectCountryInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'countryCode' || name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSignup = async () => {
    setIsLoading(true);

    if (!formData.country) {
      setMessage('Please select your country.');
      setMessageType('error');
      setIsLoading(false);
      return;
    }

    const fullPhone = `+${formData.countryCode}${formData.phoneNumber}`;
    const payload = {
      ...formData,
      phone: fullPhone
    };

    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        const loginRes = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password })
        });

        if (loginRes.status === 403) {
          const loginData = await loginRes.json();
          navigate(`/check-email?email=${encodeURIComponent(formData.email)}`);
          return;
        }

        const loginData = await loginRes.json();

        if (loginRes.ok) {
          login(loginData.token, loginData.user);
          navigate('/payment');
        }
      } else {
        setMessage(data.message || 'Signup failed');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('An error occurred during signup.');
      setMessageType('error');
    }

    setIsLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-6 bg-white rounded shadow-md">
        <img src="/ennoverse.png" alt="Ennoverse Logo" className="h-8 mx-auto mb-6" />

        <h2 className="text-xl font-semibold mb-4 text-center text-gray-700">
          Create Your Account
        </h2>

        {message && (
          <div className={`mt-2 mb-4 p-3 rounded text-sm ${
            messageType === 'success'
              ? 'bg-green-100 text-green-700 border border-green-300'
              : 'bg-red-100 text-red-700 border border-red-300'
          }`}>
            {message}
          </div>
        )}

        <input
          className="w-full p-2 border rounded mb-4"
          type="text"
          name="name"
          placeholder="Full Name"
          value={formData.name}
          onChange={handleChange}
        />

        <input
          className="w-full p-2 border rounded mb-4"
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
        />

        <input
          className="w-full p-2 border rounded mb-4"
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
        />

        <div className="flex gap-2 mb-4">
          <div className="flex items-center border rounded px-2">
            <span className="text-gray-500 select-none">+</span>
            <input
              className="w-16 p-2 outline-none"
              type="text"
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
            />
          </div>
          <input
            className="flex-1 p-2 border rounded"
            type="text"
            name="phoneNumber"
            placeholder="Phone Number"
            value={formData.phoneNumber}
            onChange={handleChange}
          />
        </div>

        <button
          onClick={handleSignup}
          disabled={isLoading}
          className="w-full bg-primary hover:bg-red-600 text-white py-2 rounded transition duration-200"
        >
          {isLoading ? 'Creating Account...' : 'Sign Up'}
        </button>
      </div>
    </div>
  );
}
